import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { getPublicProfileUrl, getVanityUrl, getBestPublicUrl } from "../lib/publicUrl";
import { toast } from "sonner";
import { ShareNetwork, Copy, Eye, CalendarBlank, ListBullets, Rows, Globe, Lock, ArrowRight, Check, User, Gear } from "phosphor-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "../context/AuthContext";

// Share mode options (Simple and Interactive only - Full Profile is via Public Profile feature)
const SHARE_MODES = [
  { value: 'simple', label: 'Simple Report', description: 'Single page with all selected sections' },
  { value: 'interactive', label: 'Interactive Report', description: 'Tabbed navigation between sections' }
];

// Default configuration values
const DEFAULT_SECTIONS = {
  global_performance: true,
  trade_type_comparison: true,
  execution_quality: true,
  planned_not_triggered: false,
  partial_execution: false,
  timeframe_breakdown: true,
  setup_performance: true
};

// Time range options
const TIME_RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' }
];

// Section groups for organized display
const SECTION_GROUPS = [
  {
    label: "Performance",
    sections: [
      { key: "global_performance", label: "Global Performance" },
      { key: "trade_type_comparison", label: "Trade Type Comparison" },
      { key: "setup_performance", label: "Setup Performance" },
      { key: "timeframe_breakdown", label: "Timeframe Breakdown" }
    ]
  },
  {
    label: "Execution",
    sections: [
      { key: "execution_quality", label: "Execution Quality" },
      { key: "planned_not_triggered", label: "Planned Trades Not Triggered" },
      { key: "partial_execution", label: "Partial Execution" }
    ]
  }
];

export default function ShareModal({ open, onOpenChange, defaultTitle = "My Trading Analytics" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [shareTitle, setShareTitle] = useState(defaultTitle);
  const [shareDescription, setShareDescription] = useState("");
  const [creatingShare, setCreatingShare] = useState(false);
  const [newlyCreatedShareUrl, setNewlyCreatedShareUrl] = useState(null);
  
  // Section configuration state
  const [shareSections, setShareSections] = useState({ ...DEFAULT_SECTIONS });
  
  // Time range filter state (default: all time)
  const [timeRange, setTimeRange] = useState('all');
  
  // Share mode state (simple or interactive)
  const [shareMode, setShareMode] = useState('simple');
  
  // Public Profile state
  const [publicProfileEnabled, setPublicProfileEnabled] = useState(null); // null = loading
  const [publicProfileLinkCopied, setPublicProfileLinkCopied] = useState(false);
  const [username, setUsername] = useState(null);

  // Fetch public profile status when modal opens
  useEffect(() => {
    if (open) {
      fetchPublicProfileStatus();
    }
  }, [open]);

  const fetchPublicProfileStatus = async () => {
    try {
      const response = await api.get('/user/profile');
      setPublicProfileEnabled(response.data.public_profile_enabled || false);
      // Store username for vanity URL
      setUsername(response.data.username || null);
    } catch (error) {
      console.error('Error fetching public profile status:', error);
      setPublicProfileEnabled(false);
    }
  };

  const copyPublicProfileLink = async () => {
    // Use vanity URL if username exists, otherwise fallback to userId URL
    const url = getBestPublicUrl(user?.id, username);
    try {
      await navigator.clipboard.writeText(url);
      setPublicProfileLinkCopied(true);
      toast.success("Public profile link copied!");
      setTimeout(() => setPublicProfileLinkCopied(false), 2000);
    } catch (error) {
      // Fallback
      try {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setPublicProfileLinkCopied(true);
        toast.success("Public profile link copied!");
        setTimeout(() => setPublicProfileLinkCopied(false), 2000);
      } catch (err) {
        toast.error("Failed to copy link");
      }
    }
  };

  const goToSettings = () => {
    onOpenChange(false);
    navigate('/settings');
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after modal closes
    setTimeout(() => {
      setShareTitle(defaultTitle);
      setShareDescription("");
      setNewlyCreatedShareUrl(null);
      setShareSections({ ...DEFAULT_SECTIONS });
      setTimeRange('all');
      setShareMode('simple');
      setPublicProfileLinkCopied(false);
    }, 200);
  };

  const createShare = async () => {
    setCreatingShare(true);
    try {
      const response = await api.post('/shares', {
        title: shareTitle,
        description: shareDescription,
        sections: shareSections,
        time_range: timeRange,
        share_type: 'analytics'  // Always analytics - full profile is via Public Profile feature
      });
      
      // Add mode parameter to URL based on share type
      const baseUrl = `${window.location.origin}/share/${response.data.id}`;
      let shareUrl = baseUrl;
      if (shareMode === 'interactive') {
        shareUrl = `${baseUrl}?mode=interactive`;
      }
      setNewlyCreatedShareUrl(shareUrl);
      toast.success("Share link created!");
    } catch (error) {
      console.error("Error creating share:", error);
      toast.error("Failed to create share link");
    } finally {
      setCreatingShare(false);
    }
  };

  const copyShareUrl = async () => {
    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(newlyCreatedShareUrl);
        toast.success("Link copied to clipboard!");
        return;
      } catch (err) {
        console.warn("Clipboard API failed, trying fallback:", err);
      }
    }
    
    // Fallback: temporary textarea + execCommand
    try {
      const textArea = document.createElement('textarea');
      textArea.value = newlyCreatedShareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success("Link copied to clipboard!");
        return;
      }
    } catch (err) {
      console.warn("Fallback copy also failed:", err);
    }
    
    // Both methods failed
    toast.error("Could not copy automatically. Please select and copy the link manually.");
  };

  // Count enabled sections for summary
  const enabledSectionCount = Object.values(shareSections).filter(Boolean).length;
  const totalSectionCount = Object.keys(shareSections).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-xl flex items-center gap-2">
            <ShareNetwork size={24} weight="duotone" className="text-[#00E676]" />
            Share Analytics
          </DialogTitle>
        </DialogHeader>

        {newlyCreatedShareUrl ? (
          <>
            <div className="py-4">
              <div className="bg-[#00E676]/10 border border-[#00E676]/30 rounded-sm p-4 mb-4">
                <p className="text-[#00E676] text-sm font-medium">Share link created successfully!</p>
              </div>
              
              <div>
                <Label className="text-white/80 mb-2 block">Share URL</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newlyCreatedShareUrl}
                    readOnly
                    onClick={(e) => e.target.select()}
                    data-testid="share-url-input"
                    className="bg-[#0A0A0A] border-white/10 text-white font-mono text-sm"
                  />
                  <Button
                    onClick={copyShareUrl}
                    variant="outline"
                    className="border-white/10 text-white hover:bg-white/5 shrink-0"
                    data-testid="copy-share-url-btn"
                  >
                    <Copy size={16} className="mr-2" />
                    Copy
                  </Button>
                </div>
                <p className="text-white/40 text-xs mt-2">Click the input to select, or use the Copy button</p>
              </div>
            </div>

            <div className="flex gap-2 mt-2">
              <Button
                onClick={() => window.open(newlyCreatedShareUrl, '_blank')}
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
                data-testid="preview-share-btn"
              >
                <Eye size={16} weight="duotone" className="mr-2" />
                Preview
              </Button>
              <Button
                onClick={handleClose}
                className="flex-1 bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90"
                data-testid="share-done-btn"
              >
                Done
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-white/60 text-sm mb-4">
              Share your trading performance with others.
            </p>

            {/* ================================================================ */}
            {/* PUBLIC PROFILE SECTION - Permanent profile link                  */}
            {/* ================================================================ */}
            <div className="mb-6 p-4 bg-gradient-to-r from-cyan-500/10 to-cyan-500/5 border border-cyan-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Globe size={18} weight="duotone" className="text-cyan-400" />
                <span className="text-cyan-400 font-semibold text-sm">Public Profile</span>
                <span className="text-cyan-400/60 text-xs ml-auto">Permanent link</span>
              </div>
              
              {publicProfileEnabled === null ? (
                // Loading state
                <div className="text-white/40 text-sm">Loading...</div>
              ) : publicProfileEnabled ? (
                // Public profile is ENABLED
                <div className="space-y-3">
                  <p className="text-white/60 text-xs">
                    Your public profile is active. Share this permanent link with anyone.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={getBestPublicUrl(user?.id, username)}
                      readOnly
                      onClick={(e) => e.target.select()}
                      className="bg-[#0A0A0A] border-cyan-500/20 text-white/80 font-mono text-xs"
                      data-testid="public-profile-url"
                    />
                    <Button
                      onClick={copyPublicProfileLink}
                      variant="outline"
                      className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 shrink-0"
                      data-testid="copy-public-profile-btn"
                    >
                      {publicProfileLinkCopied ? (
                        <Check size={16} className="text-green-400" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => window.open(getBestPublicUrl(user?.id, username), '_blank')}
                      className="text-cyan-400/70 text-xs hover:text-cyan-400 flex items-center gap-1"
                    >
                      <Eye size={12} />
                      Preview
                    </button>
                    <span className="text-white/20">·</span>
                    <button
                      onClick={goToSettings}
                      className="text-white/40 text-xs hover:text-white/70 flex items-center gap-1"
                    >
                      <Gear size={12} />
                      Manage in Settings
                    </button>
                  </div>
                </div>
              ) : (
                // Public profile is DISABLED
                <div className="space-y-3">
                  <p className="text-white/50 text-xs">
                    Enable your Public Profile to get a permanent shareable link to your full read-only GENI experience.
                  </p>
                  <div className="flex items-center gap-2">
                    <Lock size={14} className="text-white/30" />
                    <span className="text-white/30 text-xs">Currently disabled</span>
                  </div>
                  <Button
                    onClick={goToSettings}
                    variant="outline"
                    className="w-full border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 text-sm"
                    data-testid="enable-public-profile-btn"
                  >
                    <Globe size={14} className="mr-2" />
                    Enable Public Profile
                    <ArrowRight size={14} className="ml-auto" />
                  </Button>
                </div>
              )}
            </div>

            {/* ================================================================ */}
            {/* SHARE REPORTS SECTION - One-off generated links                  */}
            {/* ================================================================ */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <ShareNetwork size={16} weight="duotone" className="text-white/40" />
                <span className="text-white/60 font-medium text-sm">Share Reports</span>
                <span className="text-white/30 text-xs ml-auto">Generated links</span>
              </div>
              <p className="text-white/40 text-xs mb-4">
                Create one-time share links with custom title, time range, and section selection.
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="share-title" className="text-white/80 mb-2 block">Title</Label>
                <Input
                  id="share-title"
                  type="text"
                  value={shareTitle}
                  onChange={(e) => setShareTitle(e.target.value)}
                  placeholder="My Trading Analytics"
                  data-testid="share-title-input"
                  className="bg-[#0A0A0A] border-white/10 text-white"
                />
              </div>
              <div>
                <Label htmlFor="share-description" className="text-white/80 mb-2 block">Description (optional)</Label>
                <Input
                  id="share-description"
                  type="text"
                  value={shareDescription}
                  onChange={(e) => setShareDescription(e.target.value)}
                  placeholder="Optional description..."
                  data-testid="share-description-input"
                  className="bg-[#0A0A0A] border-white/10 text-white"
                />
              </div>
            </div>

            {/* Share Mode Selector */}
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <Rows size={14} weight="duotone" className="text-white/40" />
                <Label className="text-white/80 text-sm">Report Type</Label>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {SHARE_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    onClick={() => setShareMode(mode.value)}
                    data-testid={`share-mode-${mode.value}`}
                    className={`px-3 py-3 text-left rounded-sm border transition-colors ${
                      shareMode === mode.value
                        ? 'bg-[#00E676]/10 border-[#00E676]/50'
                        : 'bg-[#0A0A0A] border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className={`text-sm font-medium ${shareMode === mode.value ? 'text-[#00E676]' : 'text-white/80'}`}>
                      {mode.label}
                    </div>
                    <div className="text-[10px] text-white/40 mt-0.5">{mode.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Time Range Filter */}
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <CalendarBlank size={14} weight="duotone" className="text-white/40" />
                <Label className="text-white/80 text-sm">Time Range</Label>
              </div>
              <div className="flex gap-2">
                {TIME_RANGE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setTimeRange(option.value)}
                    data-testid={`time-range-${option.value}`}
                    className={`flex-1 px-3 py-2 text-xs font-mono rounded-sm border transition-colors ${
                      timeRange === option.value
                        ? 'bg-[#00E676]/10 border-[#00E676]/50 text-[#00E676]'
                        : 'bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20 hover:text-white/80'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Section Selection */}
            <div className="mt-4 p-4 bg-[#0A0A0A] border border-white/10 rounded-sm max-h-56 overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <div className="text-white/40 text-xs font-mono uppercase">Select sections to share</div>
                <div className="text-white/30 text-xs font-mono">{enabledSectionCount}/{totalSectionCount}</div>
              </div>
              
              {/* Section Groups */}
              {SECTION_GROUPS.map((group) => (
                <div key={group.label} className="mb-4 last:mb-0">
                  <div className="text-white/30 text-[10px] font-mono uppercase mb-2">{group.label}</div>
                  <div className="space-y-2">
                    {group.sections.map((section) => (
                      <label key={section.key} className="flex items-center gap-2 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          checked={shareSections[section.key]}
                          onChange={(e) => setShareSections(prev => ({...prev, [section.key]: e.target.checked}))}
                          className="w-4 h-4 rounded border-white/20 bg-transparent accent-[#00E676]"
                        />
                        <span className="text-sm text-white/70 group-hover:text-white">{section.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Privacy Notice */}
            <div className="mt-3 px-3 py-2 bg-white/5 rounded-sm">
              <p className="text-white/40 text-xs">
                Capital, P&L, and Hand Size are always hidden from shared reports. For full profile sharing, use Public Profile above.
              </p>
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                onClick={handleClose}
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
                data-testid="share-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={createShare}
                disabled={creatingShare || !shareTitle.trim()}
                className="flex-1 bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90"
                data-testid="share-create-btn"
              >
                {creatingShare ? "Creating..." : "Create Report Link"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
